import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Users, PlaySquare, Music4, Eye, ThumbsUp, LogOut } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { fmtNum } from "../config";
import { Spinner, Empty, Avatar, VideoCard, SectionTitle } from "../components/cards";

export const SignInNotice = ({ what }) => {
  const navigate = useNavigate();
  return (
    <div className="mx-auto max-w-md py-20" data-testid="signin-notice">
      <Empty>
        <p className="text-center">Sign in to {what}.</p>
        <button onClick={() => navigate("/app/login")} className="mx-auto mt-4 block rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="notice-signin-button">
          Sign in
        </button>
      </Empty>
    </div>
  );
};

const PageShell = ({ title, sub, children, testId }) => (
  <div className="mx-auto max-w-3xl" data-testid={testId}>
    <h1 className="font-heading text-2xl font-bold tracking-tight text-white">{title}</h1>
    {sub && <p className="mt-2 text-sm text-slate-500">{sub}</p>}
    <div className="mt-8">{children}</div>
  </div>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-white/[0.02] p-5 ${className}`}>{children}</div>
);

export const Dashboard = () => {
  const { user } = useAuth();
  const [videos, setVideos] = useState(null);
  const [tracks, setTracks] = useState(0);

  useEffect(() => {
    if (!user) return;
    api.get(`/videos?creatorId=${user.id}&limit=48`)
      .then((r) => (Array.isArray(r) ? r : r.videos || []).filter((v) => v.creatorId === user.id))
      .then(setVideos)
      .catch(() => setVideos([]));
    api.get("/music")
      .then((r) => (Array.isArray(r) ? r : r.tracks || []).filter((t) => t.creatorId === user.id).length)
      .then(setTracks)
      .catch(() => {});
  }, [user]);

  if (!user) return <SignInNotice what="see your dashboard" />;
  if (videos === null) return <Spinner />;

  const totalViews = videos.reduce((n, v) => n + (v.views || 0), 0);
  const totalLikes = videos.reduce((n, v) => n + (v.likes || 0), 0);

  const stats = [
    { icon: Users, label: "Subscribers", value: fmtNum(user.subscribers), testId: "stat-subscribers" },
    { icon: PlaySquare, label: "Videos", value: fmtNum(videos.length), testId: "stat-videos" },
    { icon: Music4, label: "Tracks", value: fmtNum(tracks), testId: "stat-tracks" },
    { icon: Eye, label: "Total views", value: fmtNum(totalViews), testId: "stat-views" },
    { icon: ThumbsUp, label: "Total likes", value: fmtNum(totalLikes), testId: "stat-likes" },
  ];

  return (
    <PageShell title="Dashboard" sub="How your content is doing across Michael C Streamline." testId="dashboard-page">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {stats.map(({ icon: Icon, label, value, testId }) => (
          <Card key={label} className="text-center">
            <Icon size={18} className="mx-auto text-crimson" />
            <p className="mt-2 font-heading text-2xl font-black text-white" data-testid={testId}>{value}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
          </Card>
        ))}
      </div>
      <div className="mt-10">
        <SectionTitle>Your videos</SectionTitle>
        {videos.length === 0 ? (
          <Empty>
            Nothing uploaded yet.{" "}
            <Link to="/app/upload" className="font-semibold text-crimson hover:underline">Upload your first video</Link>
          </Empty>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((v) => <VideoCard key={v.id} video={v} />)}
          </div>
        )}
      </div>
    </PageShell>
  );
};

export const Blocked = () => {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    if (!user) return;
    api.get("/me/blocked")
      .then((r) => setList(Array.isArray(r) ? r : r.users || r.blocked || []))
      .catch(() => {
        setAvailable(false);
        setList([]);
      });
  }, [user]);

  if (!user) return <SignInNotice what="manage blocked users" />;
  if (list === null) return <Spinner />;

  return (
    <PageShell title="Blocked users" sub="People you've blocked can't message or follow you." testId="blocked-page">
      {!available ? (
        <Card>
          <p className="text-sm leading-relaxed text-slate-400">
            You can block anyone from their profile page (the Block button) — that works on the web. Viewing and unblocking your blocked list isn't available on the web yet because the server doesn't expose that list to the website; use the phone app for now.
          </p>
        </Card>
      ) : list.length === 0 ? (
        <Empty>You haven't blocked anyone.</Empty>
      ) : (
        <div className="space-y-2">
          {list.map((u) => (
            <Card key={u.id || u.userId} className="flex items-center gap-3">
              <Avatar user={u} size={36} />
              <p className="min-w-0 flex-1 truncate font-heading text-sm font-semibold text-white">{u.displayName || u.username}</p>
              <button
                onClick={async () => {
                  try {
                    await api.del(`/me/blocked/${u.id || u.userId}`);
                    setList((l) => l.filter((x) => (x.id || x.userId) !== (u.id || u.userId)));
                  } catch {}
                }}
                className="rounded-full border border-white/15 px-4 py-1.5 font-heading text-xs font-bold text-slate-200 transition-colors hover:border-crimson/50 hover:text-white"
                data-testid={`unblock-${u.id || u.userId}`}
              >
                Unblock
              </button>
            </Card>
          ))}
        </div>
      )}
    </PageShell>
  );
};

export const Security = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return <SignInNotice what="manage security" />;
  return (
    <PageShell title="Security" sub="Your sign-in and account protection." testId="security-page">
      <div className="space-y-4">
        <Card>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Email</p>
          <p className="mt-2 font-heading text-base font-semibold text-white" data-testid="security-email">{user.email}</p>
        </Card>
        <Card>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Two-factor authentication</p>
          <p className={`mt-2 font-heading text-base font-bold ${user.twoFactorEnabled ? "text-emerald-400" : "text-slate-300"}`} data-testid="security-2fa-status">
            {user.twoFactorEnabled ? "Enabled" : "Not enabled"}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Setting up two-factor authentication and changing your password are done in the phone app — the server doesn't offer those to the website yet. Your status above is always live and shared with the app.
          </p>
        </Card>
        <button
          onClick={() => { logout(); navigate("/app"); }}
          className="flex items-center gap-2 rounded-full border border-crimson/40 px-5 py-2.5 font-heading text-xs font-bold text-crimson transition-colors hover:bg-crimson/10"
          data-testid="security-signout-button"
        >
          <LogOut size={14} /> Sign out of this browser
        </button>
      </div>
    </PageShell>
  );
};
